import React, { useEffect, useState } from 'react';
import { 
  Trophy, Star, Flame, Sparkles, Crown, Medal, Award,
  X, Confetti, Heart, Zap, Target
} from 'lucide-react';
import { StreakMilestone } from '@/services/learning-streak.service';

interface MilestoneCelebrationProps {
  milestone: StreakMilestone;
  currentStreak: number;
  isVisible: boolean;
  onClose: () => void;
}

interface MilestoneConfig {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  borderColor: string;
  celebration: string;
}

const getMilestoneConfig = (milestone: StreakMilestone): MilestoneConfig => {
  const configs: Record<StreakMilestone, MilestoneConfig> = {
    7: {
      title: 'First Week Warrior!',
      description: 'You\'ve completed your first week of consistent learning!',
      icon: <Medal className="w-12 h-12" />,
      color: 'text-green-600',
      bgColor: 'bg-gradient-to-br from-green-50 to-emerald-100',
      borderColor: 'border-green-200',
      celebration: '🎉'
    },
    14: {
      title: 'Two Week Champion!',
      description: 'Incredible consistency! You\'re building a strong learning habit.',
      icon: <Award className="w-12 h-12" />,
      color: 'text-blue-600',
      bgColor: 'bg-gradient-to-br from-blue-50 to-cyan-100',
      borderColor: 'border-blue-200',
      celebration: '🚀'
    },
    30: {
      title: 'Monthly Master!',
      description: 'A full month of learning! You\'re unstoppable!',
      icon: <Trophy className="w-12 h-12" />,
      color: 'text-purple-600',
      bgColor: 'bg-gradient-to-br from-purple-50 to-violet-100',
      borderColor: 'border-purple-200',
      celebration: '🌟'
    },
    50: {
      title: 'Learning Legend!',
      description: 'Fifty days of dedication! You\'re inspiring others!',
      icon: <Star className="w-12 h-12" />,
      color: 'text-orange-600',
      bgColor: 'bg-gradient-to-br from-orange-50 to-amber-100',
      borderColor: 'border-orange-200',
      celebration: '⚡'
    },
    100: {
      title: 'Centennial Scholar!',
      description: 'One hundred days! You\'ve reached extraordinary heights!',
      icon: <Crown className="w-12 h-12" />,
      color: 'text-red-600',
      bgColor: 'bg-gradient-to-br from-red-50 to-rose-100',
      borderColor: 'border-red-200',
      celebration: '👑'
    },
    365: {
      title: 'Annual Achiever!',
      description: 'A full year of learning! You are truly exceptional!',
      icon: <Flame className="w-12 h-12" />,
      color: 'text-yellow-600',
      bgColor: 'bg-gradient-to-br from-yellow-50 to-gold-100',
      borderColor: 'border-yellow-200',
      celebration: '🏆'
    }
  };

  return configs[milestone];
};

export default function MilestoneCelebration({ 
  milestone, 
  currentStreak, 
  isVisible, 
  onClose 
}: MilestoneCelebrationProps) {
  const [showConfetti, setShowConfetti] = useState(false);
  const config = getMilestoneConfig(milestone);

  useEffect(() => {
    if (isVisible) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        {/* Celebration Modal */}
        <div className={`relative bg-white rounded-2xl border-2 ${config.borderColor} ${config.bgColor} p-8 max-w-md w-full mx-4 shadow-2xl transform transition-all duration-500`}>
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/50 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>

          {/* Confetti Animation */}
          {showConfetti && (
            <div className="absolute -top-10 -left-10 -right-10 -bottom-10 pointer-events-none overflow-hidden">
              <div className="confetti-container">
                {[...Array(20)].map((_, i) => (
                  <div
                    key={i}
                    className="confetti"
                    style={{
                      '--delay': `${i * 0.1}s`,
                      '--x': `${Math.random() * 100}%`,
                      left: `${Math.random() * 100}%`,
                    } as React.CSSProperties}
                  >
                    {['🎉', '🎊', '✨', '⭐', '🌟'][Math.floor(Math.random() * 5)]}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content */}
          <div className="text-center">
            {/* Icon */}
            <div className={`${config.color} mb-4 flex justify-center relative`}>
              {config.icon}
              <div className="absolute -top-2 -right-2 text-2xl animate-bounce">
                {config.celebration}
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {config.title}
            </h2>

            {/* Streak Display */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <Flame className="w-8 h-8 text-orange-500" />
              <span className="text-4xl font-bold text-gray-900">{currentStreak}</span>
              <span className="text-lg text-gray-600">days</span>
            </div>

            {/* Description */}
            <p className="text-gray-700 mb-6 leading-relaxed">
              {config.description}
            </p>

            {/* Achievement Badges */}
            <div className="flex justify-center gap-2 mb-6">
              <div className={`inline-flex items-center gap-2 px-4 py-2 ${config.bgColor} ${config.borderColor} border rounded-full`}>
                <Target className={`w-4 h-4 ${config.color}`} />
                <span className={`text-sm font-medium ${config.color}`}>
                  Milestone Achieved
                </span>
              </div>
            </div>

            {/* Progress Visualization */}
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Streak Progress</span>
                <span>{currentStreak} / {milestone} days</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full bg-gradient-to-r ${config.color.replace('text-', 'from-').replace('-600', '-400')} ${config.color.replace('text-', 'to-').replace('-600', '-600')} transition-all duration-1000`}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-medium"
              >
                Continue Learning
              </button>
              <button
                onClick={() => {
                  // Share functionality could go here
                  onClose();
                }}
                className={`flex-1 px-6 py-3 bg-gradient-to-r ${config.color.replace('text-', 'from-').replace('-600', '-500')} ${config.color.replace('text-', 'to-').replace('-600', '-700')} text-white rounded-lg hover:shadow-lg transition-all font-medium flex items-center justify-center gap-2`}
              >
                <Heart className="w-4 h-4" />
                Share Achievement
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confetti Styles */}
      <style jsx>{`
        .confetti-container {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }
        
        .confetti {
          position: absolute;
          font-size: 20px;
          animation: confetti-fall 3s ease-out var(--delay) both;
        }
        
        @keyframes confetti-fall {
          0% {
            transform: translateY(-100px) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(400px) rotate(360deg);
            opacity: 0;
          }
        }
      `}</style>
    </>
  );
}

// Hook to detect milestone achievements
export function useMilestoneDetection(currentStreak: number, previousStreak?: number) {
  const [celebratingMilestone, setCelebratingMilestone] = useState<StreakMilestone | null>(null);
  
  const milestones: StreakMilestone[] = [7, 14, 30, 50, 100, 365];

  useEffect(() => {
    if (previousStreak !== undefined && currentStreak > previousStreak) {
      const achievedMilestone = milestones.find(
        milestone => currentStreak >= milestone && (previousStreak === undefined || previousStreak < milestone)
      );
      
      if (achievedMilestone) {
        setCelebratingMilestone(achievedMilestone);
      }
    }
  }, [currentStreak, previousStreak]);

  const closeCelebration = () => {
    setCelebratingMilestone(null);
  };

  return {
    celebratingMilestone,
    closeCelebration,
    isCelebrating: celebratingMilestone !== null
  };
}